import {
  HistoryFromField,
  defaultHistoryFrom,
  historyFromPayload,
  historyNote,
} from "@/components/analysis/history-from-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { Link, Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/agents/analyst/whatsapp` — admin-only. The list of paired (watched) chats; each
 * opens its full transcript. Top-right: "Pair new chat" and the WhatsApp
 * "Account" (link / unlink, QR) — both in modals.
 *
 * E2E behaviour contract lives in `e2e/analysis-whatsapp.spec.ts`.
 */

type Project = { id: string; name: string };
type PickerChat = { chatId: string; name: string; messageCount: number; enabled: boolean };
type PairedChat = {
  chatId: string;
  name: string;
  projectId: string | null;
  projectName: string | null;
  historyFrom: string | null;
  messageCount: number;
  processedCount: number;
  /** Messages kept only as background for the AI (older than historyFrom). */
  contextCount: number;
};
type WhatsAppStatus = {
  enabled: boolean;
  status: "disconnected" | "connecting" | "qr" | "connected";
  qr: string | null;
  me: { id: string; name?: string } | null;
  error: string | null;
  /** Outcome of the last "Load full history". History is answered by the
   *  paired phone and often doesn't come — the button must say which it was. */
  lastHistoryPull: {
    at: string;
    chats: number;
    brought: number;
    running: boolean;
  } | null;
};

export function WhatsAppPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [projects, setProjects] = useState<Project[]>([]);
  const [wa, setWa] = useState<WhatsAppStatus | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [paired, setPaired] = useState<PairedChat[]>([]);
  const [picker, setPicker] = useState<PickerChat[]>([]);
  const [historyFrom, setHistoryFrom] = useState(defaultHistoryFrom());

  const isAdmin = user.globalRole === "admin";

  const loadPaired = useCallback(
    () =>
      api
        .get<{ chats: PairedChat[] }>("/api/auto-sources/paired-chats")
        .then((r) => setPaired(r.chats))
        .catch(() => {}),
    [],
  );
  const loadPicker = useCallback(
    () =>
      api
        .get<{ chats: PickerChat[] }>("/api/auto-sources/whatsapp-chats")
        .then((r) => setPicker(r.chats))
        .catch(() => {}),
    [],
  );
  const refreshWa = useCallback(async (): Promise<WhatsAppStatus | null> => {
    try {
      const s = await api.get<WhatsAppStatus>("/api/analysis/whatsapp/status");
      setWa(s);
      return s;
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        const off: WhatsAppStatus = {
          enabled: false,
          status: "disconnected",
          qr: null,
          me: null,
          error: null,
          lastHistoryPull: null,
        };
        setWa(off);
        return off;
      }
      return null;
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get<{ projects: Project[] }>("/api/projects?pageSize=200")
      .then((r) => setProjects(r.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
    void loadPaired();
  }, [isAdmin, loadPaired]);

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    const tick = async () => {
      const s = await refreshWa();
      if (alive && s?.status === "connected") void loadPaired();
    };
    void tick();
    const id = setInterval(() => void tick(), 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [isAdmin, refreshWa, loadPaired]);

  if (!isAdmin) return <Navigate to="/" />;

  async function connectWa() {
    setConnecting(true);
    try {
      setWa(await api.post<WhatsAppStatus>("/api/analysis/whatsapp/connect", {}));
    } catch {
      toast({ title: t("agentTabs.whatsapp.pairFailed"), variant: "destructive" });
    } finally {
      setConnecting(false);
    }
  }
  async function logoutWa() {
    try {
      setWa(await api.post<WhatsAppStatus>("/api/analysis/whatsapp/logout", {}));
      setPaired([]);
    } catch {
      toast({ title: t("agentTabs.whatsapp.unlinkFailed"), variant: "destructive" });
    }
  }
  async function loadHistory() {
    try {
      const res = await api.post<{ chats: number }>("/api/auto-sources/whatsapp-history", {});
      toast({
        title: t("agentTabs.whatsapp.historyAsking"),
        description: t("agentTabs.whatsapp.historyAskingDesc", { count: res.chats }),
      });
    } catch {
      toast({ title: t("agentTabs.whatsapp.historyFailed"), variant: "destructive" });
    }
  }
  async function pairChat(chatId: string, name: string) {
    // Send the name we already show in the picker — the group may have no
    // observed messages yet, so the server can't always resolve it itself.
    await api
      .post("/api/auto-sources/paired-chats", {
        chatId,
        name,
        historyFrom: historyFromPayload(historyFrom),
      })
      .catch(() => {});
    await loadPaired();
    await loadPicker();
  }
  /** Move a watched chat's "suggest from" date. Re-pairing the same chat is
   *  an upsert on the server (it re-labels what's stored, keeps it), so this
   *  never goes near the destructive unpair. */
  async function setChatHistoryFrom(chat: PairedChat, historyFromValue: string) {
    try {
      await api.post("/api/auto-sources/paired-chats", {
        chatId: chat.chatId,
        name: chat.name,
        historyFrom: historyFromPayload(historyFromValue),
      });
      await loadPaired();
      toast({
        title: t("agentTabs.whatsapp.dateChanged"),
        description: t("agentTabs.whatsapp.dateChangedDesc"),
      });
    } catch {
      toast({ title: t("agentTabs.whatsapp.dateFailed"), variant: "destructive" });
    }
  }
  async function unpairChat(chatId: string) {
    await api
      .delete(`/api/auto-sources/paired-chats/${encodeURIComponent(chatId)}`)
      .catch(() => {});
    await loadPaired();
  }
  async function setChatProject(chatId: string, projectId: string | null) {
    setPaired((prev) =>
      prev.map((c) =>
        c.chatId === chatId
          ? { ...c, projectId, projectName: projects.find((p) => p.id === projectId)?.name ?? null }
          : c,
      ),
    );
    await api
      .patch(`/api/auto-sources/paired-chats/${encodeURIComponent(chatId)}`, { projectId })
      .catch(() => void loadPaired());
  }

  const projectOptions = projects.map((p) => ({ value: p.id, label: p.name }));
  const pairedIds = new Set(paired.map((c) => c.chatId));
  const pickable = picker.filter((c) => !c.enabled && !pairedIds.has(c.chatId));
  const connected = wa?.status === "connected";

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">WhatsApp</h2>
          <p className="text-muted-foreground mt-1">{t("agentTabs.whatsapp.intro")}</p>
        </div>
        <div className="flex gap-2">
          {connected && (
            <Dialog onOpenChange={(o) => o && loadPicker()}>
              <DialogTrigger asChild>
                <Button type="button" size="sm" data-testid="pair-new-open">
                  {t("agentTabs.whatsapp.pairNew")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t("agentTabs.whatsapp.pairTitle")}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">{t("agentTabs.whatsapp.pairHint")}</p>
                <HistoryFromField value={historyFrom} onChange={setHistoryFrom} />
                {pickable.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("agentTabs.whatsapp.nothingToPair")}
                  </p>
                ) : (
                  <ul className="max-h-80 divide-y overflow-auto rounded-md border">
                    {pickable.map((ch) => (
                      <li
                        key={ch.chatId}
                        className="flex items-center justify-between gap-3 px-3 py-2"
                        data-testid="pair-candidate"
                      >
                        <span className="text-sm">
                          <span className="font-medium">{ch.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {" "}
                            ({ch.messageCount})
                          </span>
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => pairChat(ch.chatId, ch.name)}
                        >
                          {t("agentTabs.whatsapp.pair")}
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </DialogContent>
            </Dialog>
          )}
          {/* On the page, not inside the account modal: this is what someone
              reaches for when a paired chat came out empty, and hunting for it
              behind another button is the last thing that helps then. */}
          {connected && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={loadHistory}
              data-testid="load-history"
            >
              {t("agentTabs.whatsapp.loadHistory")}
            </Button>
          )}
          <WhatsAppAccountModal
            wa={wa}
            connecting={connecting}
            onConnect={connectWa}
            onLogout={logoutWa}
          />
        </div>
      </div>

      {/* The history comes from the phone, so "Load full history" can finish
          having brought nothing at all. Saying so — and saying what does work
          instead — beats a toast that promises a background job and then
          leaves the chats exactly as empty as before. */}
      {connected && wa?.lastHistoryPull && (
        <p
          className={
            wa.lastHistoryPull.running || wa.lastHistoryPull.brought > 0
              ? "text-sm text-muted-foreground"
              : "text-sm text-amber-600 dark:text-amber-500"
          }
          data-testid="history-pull-result"
        >
          {wa.lastHistoryPull.running ? (
            <>{t("agentTabs.whatsapp.pulling", { count: wa.lastHistoryPull.chats })}</>
          ) : wa.lastHistoryPull.brought > 0 ? (
            <>{t("agentTabs.whatsapp.pulled", { count: wa.lastHistoryPull.brought })}</>
          ) : (
            <>
              <Trans i18nKey="agentTabs.whatsapp.pulledNothing" components={{ b: <strong /> }} />
            </>
          )}
        </p>
      )}

      {!connected ? (
        <p className="text-sm text-muted-foreground" data-testid="whatsapp-not-connected">
          <Trans i18nKey="agentTabs.whatsapp.notConnected" components={{ b: <strong /> }} />
        </p>
      ) : paired.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="paired-empty">
          <Trans i18nKey="agentTabs.whatsapp.pairedEmpty" components={{ b: <strong /> }} />
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {paired.map((ch) => (
            <li
              key={ch.chatId}
              className="flex flex-wrap items-center justify-between gap-3 px-3 py-2"
              data-testid="paired-chat-row"
            >
              <div className="min-w-0 flex-1">
                <Link
                  to="/analysis/chats/$chatId"
                  params={{ chatId: ch.chatId }}
                  className="font-medium underline"
                  data-testid="paired-chat-open"
                >
                  {ch.name}
                </Link>
                <span className="ml-2 text-xs text-muted-foreground">
                  {t("agentTabs.whatsapp.processed", {
                    done: ch.processedCount,
                    total: ch.messageCount,
                  })}
                </span>
                <span
                  className="ml-2 text-xs text-muted-foreground"
                  data-testid="history-from-note"
                >
                  {historyNote(ch.historyFrom, ch.contextCount)}
                </span>
                {/* Changing the date must not mean unpair + pair again: the
                    messages hang off the pairing row by a cascading foreign
                    key, so unpairing throws the whole stored chat away. */}
                <ChangeHistoryFromDialog chat={ch} onSave={setChatHistoryFrom} />
              </div>
              <div className="flex items-center gap-2">
                <Select
                  className="w-44"
                  value={ch.projectId ?? ""}
                  onChange={(v) => setChatProject(ch.chatId, v || null)}
                  aria-label={t("agentTabs.whatsapp.projectFor", { name: ch.name })}
                  options={[
                    { value: "", label: t("agentTabs.whatsapp.hubertPicks") },
                    ...projectOptions,
                  ]}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => unpairChat(ch.chatId)}
                  data-testid="unpair-chat"
                >
                  {t("agentTabs.whatsapp.unpair")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Change where a watched chat's suggestions start, in place. "no history
 * loaded" means the chat was paired without a date and no history is ever
 * asked for — and the only way to fix that used to be unpairing, which
 * cascades the stored messages away with the pairing.
 */
function ChangeHistoryFromDialog({
  chat,
  onSave,
}: {
  chat: PairedChat;
  onSave: (chat: PairedChat, historyFrom: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(chat.historyFrom?.slice(0, 10) ?? defaultHistoryFrom());

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="ml-1 h-auto p-0 text-xs"
          data-testid="change-history-from"
        >
          {t("agentTabs.whatsapp.change")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("agentTabs.whatsapp.suggestionsFrom", { name: chat.name })}</DialogTitle>
        </DialogHeader>
        <HistoryFromField value={value} onChange={setValue} />
        <p className="text-sm text-muted-foreground">{t("agentTabs.whatsapp.relabelHint")}</p>
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            data-testid="save-history-from"
            onClick={async () => {
              await onSave(chat, value);
              setOpen(false);
            }}
          >
            {t("agentTabs.whatsapp.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The link / unlink (+ QR) flow, in a modal opened by the "Account" button. */
function WhatsAppAccountModal({
  wa,
  connecting,
  onConnect,
  onLogout,
}: {
  wa: WhatsAppStatus | null;
  connecting: boolean;
  onConnect: () => void;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid="whatsapp-account-open">
          {t("agentTabs.whatsapp.account")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("agentTabs.whatsapp.accountTitle")}</DialogTitle>
        </DialogHeader>
        {wa === null ? (
          <p className="text-sm text-muted-foreground">{t("agentTabs.whatsapp.loading")}</p>
        ) : !wa.enabled ? (
          <p className="text-sm text-muted-foreground" data-testid="whatsapp-off">
            <Trans i18nKey="agentTabs.whatsapp.off" components={{ c: <code /> }} />
          </p>
        ) : wa.status === "connected" ? (
          <div className="space-y-3" data-testid="whatsapp-connected">
            <p className="text-sm">
              <Trans
                i18nKey="agentTabs.whatsapp.linkedAs"
                values={{ name: wa.me?.name ?? wa.me?.id ?? "—" }}
                components={{ b: <span className="font-medium" /> }}
              />
            </p>
            <p className="text-sm text-muted-foreground">{t("agentTabs.whatsapp.relinkHint")}</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onLogout}>
                {t("agentTabs.whatsapp.unlink")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="whatsapp-pairing">
            {wa.status === "qr" && wa.qr ? (
              <div className="space-y-2">
                <p className="text-sm">
                  <Trans i18nKey="agentTabs.whatsapp.scan" components={{ b: <strong /> }} />
                </p>
                <img
                  src={wa.qr}
                  alt={t("agentTabs.whatsapp.qrAlt")}
                  data-testid="whatsapp-qr"
                  className="h-64 w-64 rounded-md border bg-white p-2"
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {wa.status === "connecting"
                  ? t("agentTabs.whatsapp.connecting")
                  : t("agentTabs.whatsapp.linkPrompt")}
              </p>
            )}
            {wa.error && <p className="text-xs text-destructive">{wa.error}</p>}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onConnect}
              disabled={connecting || wa.status === "connecting" || wa.status === "qr"}
              data-testid="whatsapp-connect"
            >
              {wa.status === "qr" || wa.status === "connecting"
                ? t("agentTabs.whatsapp.waitingScan")
                : connecting
                  ? t("agentTabs.whatsapp.starting")
                  : t("agentTabs.whatsapp.link")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
